import { Component, DestroyRef, inject, Signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { map, startWith } from 'rxjs';

import { CheckboxItem } from '@celum/common-components/checkbox-item';
import { ColorConstants } from '@celum/common-components/constants';
import { CelumIcon, IconConfiguration } from '@celum/common-components/icon';
import { CelumFormFieldDirective } from '@celum/common-components/inputs';
import { FileExtensionGroup } from '@celum/formats/domain';
import { FormatFormService, SpecificationStore } from '@celum/formats/shared';
import { CelumChipComponent, CelumChipSetComponent } from '@celum/shared/ui';

@Component({
  selector: 'file-extensions',
  templateUrl: './file-extensions.component.html',
  styleUrls: ['./file-extensions.component.scss', '../format-view-shared.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    FormsModule,
    MatSelectModule,
    ReactiveFormsModule,
    TranslatePipe,
    CelumChipComponent,
    CelumChipSetComponent,
    MatAutocompleteModule,
    CelumFormFieldDirective,
    MatInput,
    CelumIcon,
    CheckboxItem
  ]
})
export class FileExtensionsComponent {
  protected readonly arrowIcon = IconConfiguration.xSmall('arrow-down-xs').withColor(ColorConstants.BLUE_GRAY_900).withIconSize(12);

  protected searchTerm = new FormControl('');

  protected formatFormService = inject(FormatFormService);
  protected specStore = inject(SpecificationStore);
  protected filteredExtensions: Signal<FileExtensionGroup[]>;

  private destroyRef = inject(DestroyRef);

  constructor() {
    this.filteredExtensions = toSignal(
      this.searchTerm.valueChanges.pipe(
        startWith(''),
        map(value => {
          return this.specStore.fileExtensionsGroups().reduce((acc, { name, extensions }) => {
            const searchTerm = (value ?? '').toLowerCase();

            if (name.toLowerCase().includes(searchTerm)) {
              return [...acc, { name, extensions }];
            }

            if (extensions.some(extension => extension.toLowerCase().includes(searchTerm))) {
              return [...acc, { name, extensions: extensions.filter(extension => extension.toLowerCase().includes(searchTerm)) }];
            }

            return acc;
          }, []);
        })
      )
    );

    this.formatFormService.formatForm.controls.fileExtension.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(status => {
      if (status === 'INVALID') {
        this.searchTerm.setErrors({ invalid: true });
        this.searchTerm.markAsTouched();

        return;
      }

      this.searchTerm.setErrors(null);
    });
  }

  protected checkFileExtensions(): void {
    if (this.formatFormService.formatForm.controls.fileExtension.value.length === this.specStore.fileExtensions().length) {
      this.formatFormService.clearFileExtensions();

      return;
    }

    this.formatFormService.selectAllFileExtensions();
  }
}
